'use client';

import { useDrawer } from '@cat-ecom/pcc-components';
import { returnFocusToNavItem } from 'app/[lang]/[esite]/(core)/_header/utils/common-utils';
import {
  createContext,
  useContext,
  FC,
  PropsWithChildren,
  useMemo,
  useCallback,
  useState
} from 'react';

interface MyEquipmentDrawerContext {
  addEquipmentDrawer: ReturnType<typeof useDrawer>;
  selectedEquipmentDrawer: ReturnType<typeof useDrawer>;
  editEquipmentDrawer: ReturnType<typeof useDrawer>;
  deleteEquipmentDrawer: ReturnType<typeof useDrawer>;
  selectEquipmentDrawer: ReturnType<typeof useDrawer>;
  closeAllDrawers: () => void;
  isLoading: boolean;
  setIsLoading: (isLoading) => void;
  setLastActiveElement: (activeElement) => void;
}

// Create context
const MyEquipmentDrawerContext = createContext<MyEquipmentDrawerContext>(null);

export const useMyEquipmentDrawer = () => useContext(MyEquipmentDrawerContext);

// create Provider
export const MyEquipmentDrawerProvider: FC<PropsWithChildren> = ({
  children
}) => {
  const [lastActiveElement, setLastActiveElement] = useState('');
  // create drawers
  const addEquipmentDrawer = useDrawer({
    onClose: () => {
      if (lastActiveElement !== '') {
        returnFocusToNavItem(lastActiveElement);
        setLastActiveElement('');
      }
    }
  });
  const selectedEquipmentDrawer = useDrawer();
  const editEquipmentDrawer = useDrawer();
  const deleteEquipmentDrawer = useDrawer();
  const selectEquipmentDrawer = useDrawer();

  const closeAllDrawers = useCallback(() => {
    addEquipmentDrawer.closeDrawer();
    selectedEquipmentDrawer.closeDrawer();
    editEquipmentDrawer.closeDrawer();
    deleteEquipmentDrawer.closeDrawer();
    selectEquipmentDrawer.closeDrawer();
  }, [
    addEquipmentDrawer,
    deleteEquipmentDrawer,
    editEquipmentDrawer,
    selectEquipmentDrawer,
    selectedEquipmentDrawer
  ]);
  const [isLoading, setIsLoading] = useState(false);

  const drawers = useMemo(() => {
    return {
      addEquipmentDrawer,
      deleteEquipmentDrawer,
      editEquipmentDrawer,
      selectEquipmentDrawer,
      selectedEquipmentDrawer,
      closeAllDrawers,
      isLoading,
      setIsLoading,
      setLastActiveElement
    };
  }, [
    addEquipmentDrawer,
    deleteEquipmentDrawer,
    editEquipmentDrawer,
    selectEquipmentDrawer,
    selectedEquipmentDrawer,
    closeAllDrawers,
    isLoading,
    setIsLoading,
    setLastActiveElement
  ]);

  return (
    <MyEquipmentDrawerContext.Provider value={drawers}>
      {children}
    </MyEquipmentDrawerContext.Provider>
  );
};
